import {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IssueOnSingleView } from 'components/dataProviders/withIssue/IssueOnSingleView';
import { logError } from 'setup/logger/logError';
import { documentClient } from 'shared/domain/document/documentClient';
import type { AttachmentCountsOfIssues } from 'shared/domain/document/documentContract';
import { IssueModel } from 'shared/domain/issue/issueModel';
import { useGetAllFilteredIssues } from 'store/issue';

export type MultiIssueFilteredIssues = {
  items: IssueOnSingleView[];
  issueModels: IssueModel[];
  /** The issues' own attachments, for the page estimate; empty when they could not be counted. */
  attachmentCountsOfIssues: AttachmentCountsOfIssues;
  isLoading: boolean;
  loaded: boolean;
};

const EMPTY_FILTERED_ISSUES: MultiIssueFilteredIssues = {
  items: [],
  issueModels: [],
  attachmentCountsOfIssues: {},
  isLoading: false,
  loaded: false,
};

/** Without the counts the estimate only misses the attachments: the report itself still works. */
async function countAttachmentsOfIssues(issueModels: IssueModel[]): Promise<AttachmentCountsOfIssues> {
  try {
    return await documentClient.countAttachmentsOfIssues(issueModels.map(({ _id }) => _id));
  } catch (error: unknown) {
    logError('Report page estimate could not count the attachments of the issues', error);
    return {};
  }
}

const MultiIssueFilteredIssuesContext = createContext<MultiIssueFilteredIssues>(
  EMPTY_FILTERED_ISSUES
);

export function useMultiIssueFilteredIssues(): MultiIssueFilteredIssues {
  return useContext(MultiIssueFilteredIssuesContext);
}

/**
 * Fetches the filtered issue set once per dialog open and shares it with both the
 * section-availability and page-estimate providers (avoids two identical network calls).
 */
export function MultiIssueFilteredIssuesProvider({
  open,
  children,
}: {
  open: boolean;
  children: ReactNode;
}): ReactElement {
  const getAllFilteredIssues = useGetAllFilteredIssues();
  const getAllRef = useRef(getAllFilteredIssues);
  getAllRef.current = getAllFilteredIssues;

  const [state, setState] = useState<MultiIssueFilteredIssues>(
    EMPTY_FILTERED_ISSUES
  );

  useEffect(() => {
    if (!open) {
      setState(EMPTY_FILTERED_ISSUES);
      return;
    }

    let cancelled = false;
    setState({ ...EMPTY_FILTERED_ISSUES, isLoading: true });

    getAllRef
      .current()
      .then(async ({ items, issueModels }) => {
        const attachmentCountsOfIssues = await countAttachmentsOfIssues(issueModels);
        if (!cancelled) {
          setState({ items, issueModels, attachmentCountsOfIssues, isLoading: false, loaded: true });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ ...EMPTY_FILTERED_ISSUES, loaded: true });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const value = useMemo(() => state, [state]);

  return (
    <MultiIssueFilteredIssuesContext.Provider value={value}>
      {children}
    </MultiIssueFilteredIssuesContext.Provider>
  );
}
